import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _get from 'lodash/get';
import { noop } from 'rxjs';
import { DEFAULT_TOKEN, persistPageSize } from 'services/PaginationHelper';
import { scrollContainerToActiveChild } from 'services/utils';
import { CREATE_PROJECT } from 'services/Permissions';
import { cloneProject } from 'services/ProjectHelper';
import { LAST_VISITED_PROJECTS, LAST_VISITED_ORGANIZATION } from 'services/StudioProfileService';
import { getStatusMessage, animateSensorIcon, cconIs2ndGen, PROBE_STATES } from 'services/SensorHelper';
import { loadMapboxSDK } from 'services/LazyLoading';
import Config from 'services/config/local';
import imgCCON from '../../../assets/images/img_ccon.png';
import imgCCONv2 from '../../../assets/images/img_ccon_v2.png';
import imgMotion from '../../../assets/images/img_motion.png';
import imgCO2 from '../../../assets/images/img_co2.png';
import imgTemperatureHumidity from '../../../assets/images/img_temperature-humidity-side-illustration.png'
import imgProbeNotConnected from '../../../assets/images/probe_not_connected.png'
import imgProbe2WireConnected from '../../../assets/images/probe_2_wire.png'
import imgProbe3WireConnected from '../../../assets/images/probe_3_wire.png'
import imgProbe4WireConnected from '../../../assets/images/probe_4_wire.png'
import { States } from '../../../app.router';

const PAGE_SIZE_SUFFIX = 'projects';

function hasErrors(project) {
    return project.alertsSummary && project.alertsSummary.total.activeErrors > 0;
}

function hasWarnings(project) {
    return project.alertsSummary && project.alertsSummary.total.activeWarnings > 0;
}

/* @ngInject */
export default class ProjectsController {
    constructor(
        IAMService,
        DialogService,
        ToastService,
        $q,
        $state,
        $stateParams,
        ProjectManager,
        $timeout,
        AnalyticsService,
        SensorService,
        StudioProfileService,
        $scope,
        $rootScope
    ) {
        this.IAMService = IAMService;
        this.DialogService = DialogService;
        this.toastService = ToastService;
        this.$q = $q;
        this.$state = $state;
        this.$stateParams = $stateParams;
        this.ProjectManager = ProjectManager;
        this.$timeout = $timeout;
        this.AnalyticsService = AnalyticsService;
        this.SensorService = SensorService;
        this.StudioProfileService = StudioProfileService;
        this.scope = $scope;
        this.$rootScope = $rootScope;
        this.imgCCON = imgCCON;
        this.imgCCONv2 = imgCCONv2;
        this.motionIcon = imgMotion;
        this.co2Icon = imgCO2;
        this.imgTemperatureHumidity = imgTemperatureHumidity;
        this.imgProbeNotConnected = imgProbeNotConnected
        this.imgProbe2WireConnected = imgProbe2WireConnected
        this.imgProbe3WireConnected = imgProbe3WireConnected
        this.imgProbe4WireConnected = imgProbe4WireConnected
        this.organizations = [];
    }

    get hasNoProjects() {
        const loadedWithoutErrors = !this.isFetching && this.firstFetchCompleted && !this.firstFetchError;
        const noSpecialCases = !this.query && !this.nextPageToken;
        return loadedWithoutErrors && noSpecialCases && !this.projects.length;
    }

    get noProjectsFound() {
        const noPagination = this.currentPageToken === DEFAULT_TOKEN && !this.nextPageToken;
        return !this.isFetching && this.query && !this.projects.length && noPagination;
    }

    // TODO: This currently use the search result to determine to show
    // this banner, but we should show this if the user has access to
    // any project with errors.  Need API support to be able to do
    // this efficiently.
    get showWarningBanner() {
        let ret = false;
        for (let i = 0; i < this.projects.length; i++) {
            if (hasErrors(this.projects[i])) {
                return false;
            }
            ret = ret || hasWarnings(this.projects[i]);
        }
        return ret;
    }

    get showErrorBanner() {
        return this.projects.reduce((acc, elem) => acc || hasErrors(elem), false);
    }

    get foundSensor() {
        return Object.keys(this.device).length;
    }

    get statusMessage() {
        return getStatusMessage(this.device);
    }

    get foundProject() {
        return Object.keys(this.project).length;
    }

    get isProbeSensor() {
        return this.device?.productNumber?.includes('102772') || this.device?.productNumber?.includes('102774')
    }

    get isTemperatureAndHumiditySensor() { // Large form factor
        // Emulated sensors use a label to inject the product number
        if (this.device?.labels?.['emulated-product-number'] === '102892') {
            return true
        }
        return this.device?.productNumber?.includes('102892') || this.device?.productNumber?.includes('102895') || this.device?.productNumber?.includes('102988')
    }

    get probeIcon() {
        switch (this.probeState) {
            case PROBE_STATES.NOT_CONNECTED || PROBE_STATES.PROBE_WIRE_STATE_UNKNOWN:
                return this.imgProbeNotConnected
            case PROBE_STATES.TWO_WIRE:
                return this.imgProbe2WireConnected
            case PROBE_STATES.THREE_WIRE:
                return this.imgProbe3WireConnected
            case PROBE_STATES.FOUR_WIRE:
                return this.imgProbe4WireConnected
            default:
                return this.imgProbeNotConnected
        }
    }


    $onInit() {
        this.query = this.$stateParams.query;
        this.showCreateProjectButton = true;

        this.currentPageSize = 100;
        this.currentPageToken = DEFAULT_TOKEN;
        this.nextPageToken = null;

        this.selectedProject = null;
        this.selectedOrganization = null;
        this.isSingleOrganization = false;

        this.firstFetchCompleted = false;
        this.firstFetchError = false;
        this.isFetching = false;
        this.projects = [];
        this.query = "";

        this.searchType = 'projects'
        this.deviceSearch = false

        this.deviceInput = ''
        this.showMissingDeviceAccess = false

        // Device ID Lookup
        this.device = {};
        this.project = {};
        this.lookupIDFetching = false;

        this.lastVisitedProjectsIds = []

        this.organizations = [];
        this.organizationPermissions = {};
        this.canCreateProjectInSelectedOrg = false;

        // Map-related variables for project creation
        this.createProjectMap = null;
        this.createProjectMarker = null;
        this.createProjectGeocoder = null;
        this.selectedTimeZone = null;
        this.selectedLatitude = null;
        this.selectedLongitude = null;
        this.showCreateProjectLocationDialog = false;
        this.timeZoneToBeConfirmed = null;
        this.loadingCreateProjectTimeZone = false;
        this.mapInitialized = false;

        // Labels for project creation
        this.projectLabels = {};
        this.showAddLabelForm = false;
        this.newLabelKey = '';
        this.newLabelValue = '';

        // Autocomplete state for labels
        this.showKeyDropdown = false;
        this.showValueDropdown = false;
        this.filteredLabelKeys = [];
        this.filteredLabelValues = [];
        this.selectedKeyIndex = -1;
        this.selectedValueIndex = -1;

        // Fetch available label keys for autocomplete
        this.SensorService.listLabelKeys().then(response => {
            // Extract project label keys for project labels (no count text)
            this.availableProjectLabelKeys = [];
            if (response.projectLabelKeys) {
                this.availableProjectLabelKeys.push(...response.projectLabelKeys.map(item => ({
                    key: item.key,
                    deviceCount: item.deviceCount,
                    type: 'project'
                })));
            }
            // Sort by device count (descending) to show most used labels first
            this.availableProjectLabelKeys.sort((a, b) => b.deviceCount - a.deviceCount);
        }).catch(error => {
            console.error('Error fetching label keys:', error); // eslint-disable-line no-console
            this.availableProjectLabelKeys = [];
        });
        
        this.fetchAllOrganizations().then((orgs) => {
            if (orgs.length <= 1) {
                this.isSingleOrganization = true;
            }
            
            // Store all organizations that the user has access to immediately
            this.organizations = orgs;
            
            // Try to select the last visited organization first
            const lastVisitedOrgName = this.StudioProfileService.get(LAST_VISITED_ORGANIZATION);
            const lastVisitedOrg = lastVisitedOrgName ? _find(orgs, { name: lastVisitedOrgName }) : null;
            
            // Fallback to current project organization, then first organization
            const currentOrg = _find(orgs, { name: this.ProjectManager.currentProject.organization });
            this.selectedOrganization = lastVisitedOrg || currentOrg || orgs[0];

            this.requestParams = {
                query: this.query,
                pageSize: this.currentPageSize,
                pageToken: this.currentPageToken,
                organization: this.selectedOrganization ? this.selectedOrganization.name : null
            };

            // Start fetching projects immediately
            this.fetchProjects().then(() => this.$timeout(scrollContainerToActiveChild));
            
            // Fetch permissions for all organizations in parallel (for create project button)
            const promises = orgs.map(org => this.IAMService.organizationPermissions(org.name));
            this.$q.all(promises).then((permissions) => {
                // Store permissions for each organization
                for (let i = 0; i < orgs.length; i++) {
                    this.organizationPermissions[orgs[i].name] = permissions[i].data;
                }
                this.updateCreateProjectPermission();
            });
        });
    }

    selectProject(project) {

        if (project.id === this.ProjectManager.currentProjectId) { // Selected the current Project
            this.closeModal() 
        }

        this.ProjectManager.setCurrentProject(cloneProject(project));
        
        // Store the organization of the selected project as the last visited organization
        if (project.organization) {
            this.StudioProfileService.set(LAST_VISITED_ORGANIZATION, project.organization);
        }

        setTimeout(function () {
            if (this.$state.current.name === States.DASHBOARD || this.$state.current.name === States.DASHBOARD_SPECIFIED) {
                this.$state.go(States.DASHBOARD, {
                    projectId: project.id
                })
            } else if (this.$state.current.name === States.SENSOR_DETAILS) {
                this.$state.go(States.SENSORS, {
                    projectId: project.id
                })
            } else if (this.$state.current.name === States.RULE_DETAIL) {
                this.$state.go(States.RULES, {
                    projectId: project.id
                })
            } else if (this.$state.current.name === States.DATA_CONNECTOR_DETAILS) {
                this.$state.go(States.DATA_CONNECTORS, {
                    projectId: project.id
                })
            } else if (this.$state.current.name === States.SERVICE_ACCOUNT_DETAILS) {
                this.$state.go(States.SERVICE_ACCOUNTS, {
                    projectId: project.id
                })
            } else if (this.$state.current.name === States.EMULATOR_DETAILS) {
                this.$state.go(States.EMULATOR, {
                    projectId: project.id
                })
            } else if (this.$state.current.name === States.PROJECTS) {
                this.$state.go(States.SENSORS, {
                    projectId: project.id
                })
            } else {
                this.$state.go(this.$state.current, {
                    projectId: project.id
                })
            }
        }.bind(this), 200); // Short delay to visually indicate project selection
    }

    fetchAllOrganizations() {
        const deferred = this.$q.defer();
        this.getOrganizationsPage(null, [])
            .then(orgs => deferred.resolve(orgs))
            .catch(() => {
                this.ToastService.showSimpleTranslated('organization_list_error');
                // Automatically close the dialog if this fails.
                deferred.reject();
            });
        return deferred.promise;
    }

    getOrganizationsPage(pageToken, previousData) {
        return this.IAMService
            .organizations({ pageToken })
            .then(({ data, nextPageToken }) => {
                const allData = [...previousData, ...data];
                if (nextPageToken !== '') {
                    return this.getOrganizationsPage(nextPageToken, allData);
                }
                return allData;
            });
    }

    getOrganizations(searchText) {
        return this.organizations.then((organizations) => {
            if (searchText === '') {
                return organizations;
            }
            const lowerSearchTerm = searchText.toLowerCase();
            return _filter(organizations, o => o.displayName.toLowerCase().search(lowerSearchTerm) !== -1);
        });
    }

    projectQueryChange(query) {
        // Catch case when user clears input
        return query === "" ? this.loadDataByQuery(query) : noop
    }

    loadDataByQuery(query) {
        this.query = query;
        this.requestParams.query = query;
        this.requestParams.pageToken = DEFAULT_TOKEN;
        return this.fetchProjects();
    }

    onOrganizationChange() {
        this.updateCreateProjectPermission();
        this.requestParams.organization = this.selectedOrganization ? this.selectedOrganization.name : null;
        this.requestParams.pageToken = DEFAULT_TOKEN;
        this.requestParams.query = this.query;
        
        // Store the last visited organization
        if (this.selectedOrganization) {
            this.StudioProfileService.set(LAST_VISITED_ORGANIZATION, this.selectedOrganization.name);
        }
        
        this.fetchProjects();
    }

    updateCreateProjectPermission() {
        if (this.selectedOrganization && this.organizationPermissions[this.selectedOrganization.name]) {
            this.canCreateProjectInSelectedOrg = this.organizationPermissions[this.selectedOrganization.name].includes(CREATE_PROJECT);
        } else {
            this.canCreateProjectInSelectedOrg = false;
        }
    }

    showCreateForm() {
        this.showCreateProjectButton = false;
        this.showCreateProjectForm = true;
        
        // Initialize map when form is shown
        if (!this.mapInitialized) {
            this.$timeout(() => {
                this.initializeCreateProjectMap();
            }, 100); // Small delay to ensure DOM is ready
        }
    }

    showCreateBtn() {
        this.showCreateProjectButton = true;
        this.showCreateProjectForm = false;
        this.name = null;
        
        // Reset map state
        this.selectedTimeZone = null;
        this.selectedLatitude = null;
        this.selectedLongitude = null;
        this.showCreateProjectLocationDialog = false;
        this.timeZoneToBeConfirmed = null;
        this.loadingCreateProjectTimeZone = false;
        
        // Reset labels
        this.projectLabels = {};
        this.showAddLabelForm = false;
        this.newLabelKey = '';
        this.newLabelValue = '';
        
        // Reset map initialization so it can be recreated next time
        this.mapInitialized = false;
        if (this.createProjectMap) {
            this.createProjectMap.remove();
            this.createProjectMap = null;
        }
        this.createProjectMarker = null;
        this.createProjectGeocoder = null;
    }

    submit() {
        this.processing = true;
        this.IAMService.createProject({
            displayName: this.name,
            organization: this.selectedOrganization.name
        })
            .then((project) => {
                const promises = [];
                const updatedProject = project;
                
                // If location is selected, update the project with location data
                if (this.selectedTimeZone && this.selectedLatitude && this.selectedLongitude) {
                    promises.push(
                        this.IAMService.updateProject(project.name, {
                            location: {
                                timeLocation: this.selectedTimeZone,
                                latitude: this.selectedLatitude,
                                longitude: this.selectedLongitude
                            }
                        }).then(locationUpdatedProject => {
                            // Update the project object with location data
                            updatedProject.location = locationUpdatedProject.location;
                            return locationUpdatedProject;
                        })
                    );
                }
                
                // If labels are provided, update the project with labels
                if (this.projectLabels && Object.keys(this.projectLabels).length > 0) {
                    // Temporarily set the current project for the ProjectManager to work
                    const previousProject = this.ProjectManager.currentProject;
                    this.ProjectManager.setCurrentProject(project);
                    
                    promises.push(
                        this.ProjectManager.updateProjectLabels(this.projectLabels)
                            .catch(() => {
                                // Don't fail the entire project creation if labels fail
                                this.toastService.showSimpleText('Project created, but failed to add labels');
                            })
                            .finally(() => {
                                // Restore previous project
                                this.ProjectManager.setCurrentProject(previousProject);
                            })
                    );
                }
                
                // Wait for all updates to complete before navigation
                return this.$q.all(promises).then(() => updatedProject);
            })
            .then((project) => {
                setTimeout(() => {
                    this.projects.unshift(project);
    
                    this.name = null;
                    this.addProjectForm.$setPristine();
                    this.addProjectForm.$setUntouched();
    
                    this.showCreateBtn();
    
                    this.toastService.showSimpleTranslated('project_was_created');
    
                    // Set the updated project in ProjectManager before navigation
                    this.ProjectManager.setCurrentProject(project);
    
                    this.$state.go(States.SENSORS, {
                        projectId: project.id,
                        justCreated: true,
                        project
                    });
                }, 300);
            }, (serverResponse) => {
                this.toastService.showSimpleTranslated('project_wasnt_created', {
                    serverResponse
                });
            })
            .finally(() => {
                this.processing = false;
            });
    }

    setPageToken({ pageToken }) {
        this.requestParams.pageToken = pageToken;
        this.fetchProjects();
    }

    setPageSize({ pageSize }) {
        persistPageSize(pageSize, PAGE_SIZE_SUFFIX);
        this.requestParams.pageToken = DEFAULT_TOKEN;
        this.requestParams.pageSize = pageSize;
        this.fetchProjects();
    }

    onSearchTypeChange() {
        if (this.searchType === 'device') {
            this.deviceSearch = true
            this.projects = []
            this.deviceInput = this.query
            this.query = ''
            setTimeout(() => {
                document.getElementById("deviceInput").focus();
                if (this.deviceInput) {
                    this.onDeviceInputChange()
                }
            }, 600);
        } else {
            this.deviceSearch = false
            this.loadDataByQuery('')
            this.deviceInput = ''
            setTimeout(() => {
                document.getElementById("autoCompleteId").focus();
            }, 400);
        }
    }

    onDeviceInputChange() {
        if (this.deviceInput.length === 20 || this.deviceInput.length === 23) {
            this.getDevice(this.deviceInput)
        } else {
            this.device = {}
        }
    }

    getDevice(deviceId) {
        // Search across all the users projects by setting project to '-'
        this.showMissingDeviceAccess = false
        this.lookupIDFetching = true;

        if (this.eventSubscription) {
            this.eventSubscription.unsubscribe();
        }

        this.SensorService.getSensor('-', deviceId).then(device => {
            const projectId = device.name.split('/')[1];
            // Listen for device events (show live touches)
            this.eventSubscription = this.SensorService
                .createObservableFromThingUpdates(device.id, projectId)
                .subscribe(event => this.onNewEvents(event));
            this.IAMService.getProject(projectId).then(project => {
                this.project = project;
                this.device = device;
                if (this.isProbeSensor) {
                    this.probeState = _get(device, 'reported.probeWireStatus.state', PROBE_STATES.NOT_CONNECTED)
                }
                this.projects = [this.project];
                this.lookupIDFetching = false;
            });

            this.AnalyticsService.trackEvent('device_id_lookup.found')
        }).catch((err) => {
            this.AnalyticsService.trackEvent(`device_id_lookup.${err.status === 404 ? "not_found" : `error.${err.status}`}`)
            this.showMissingDeviceAccess = true
            this.device = {};
            this.lookupIDFetching = false;
        }).finally(() => {
            this.firstFetchCompleted = true;
            this.isFetching = false;
        });
    }

    ccon2ndGen(device) { // eslint-disable-line class-methods-use-this
        return cconIs2ndGen(device)
    } 


    fetchProjects() {
        this.isFetching = true;
        this.projects = [];
        this.device = {};

        const deferred = this.$q.defer();
        this.IAMService.projects(this.requestParams).then(({ data, nextPageToken }) => {
            this.projects = data;
            deferred.resolve([]);

            if (this.requestParams.query) {
                // The user has entered a search string, so we want to track the search results
                this.AnalyticsService.trackEvent(`project_search.${data.length > 0 ? 'found' : 'not_found'}`)
            }
            
            // Show recently viewed projects if the user has many projects and an empty search
            if (this.projects.length >= 10 && this.requestParams.query === "") {

                const lastVisitedProjectsIds = this.StudioProfileService.get(LAST_VISITED_PROJECTS) || []
                this.lastVisitedProjectsIds = [...lastVisitedProjectsIds]
                
                // Remove current project from list of recently visited projects
                const currentProjectIndex = this.lastVisitedProjectsIds.indexOf(this.ProjectManager.currentProjectId)
                if (currentProjectIndex > -1) {
                    this.lastVisitedProjectsIds.splice(currentProjectIndex, 1)
                }

                // Recently viewed projects are shown at the top by having a rank >0, the remaining will have -1
                this.projects.forEach(project => {
                    project.lastVisitedRank = this.lastVisitedProjectsIds.indexOf(project.id) // -1 if not found
                });
    
                // Optionally fetch recently viewed projects if they're not already fetched
                // When organization filtering is active, only fetch projects from the same organization
                const projectIds = this.projects.map(project => project.id)
                const selectedOrgName = this.requestParams.organization;
                
                for (let index = 0; index < this.lastVisitedProjectsIds.length; index++) {
                    const projectId = this.lastVisitedProjectsIds[index]
                    if (!projectIds.includes(projectId)) {
                        this.IAMService.getProject(projectId).then(project => {
                            // Only add the project if it belongs to the selected organization (or no org filter)
                            if (!selectedOrgName || project.organization === selectedOrgName) {
                                project.lastVisitedRank = index
                                this.projects.push(project)
                            }
                        }).catch(() => {
                            // Ignore errors for projects that might not exist anymore or user lost access to
                        })
                    }
                }
            }

            this.currentPageSize = this.requestParams.pageSize;
            this.currentPageToken = this.requestParams.pageToken;
            this.nextPageToken = nextPageToken;
        })
            .catch((serverResponse) => {
                if (!this.firstFetchCompleted) {
                    this.firstFetchError = true;
                }
                this.toastService.showSimpleTranslated('projects_wasnt_fetched', {
                    serverResponse
                });
                deferred.reject();
            })
            .finally(() => {
                this.isFetching = false;
                this.firstFetchCompleted = true;
            });
        return deferred.promise;
    }

    onNewEvents(event) {
        this.device.reported = {
            ...this.device.reported,
            ...event.data
        }
        if (event.eventType === 'probeWireStatus') {
            this.probeState = _get(this.device, 'reported.probeWireStatus.state', PROBE_STATES.NOT_CONNECTED);
        }
        if (event.eventType === 'touch') {
            this.rippleOnSensor();
        }
        this.scope.$applyAsync();
    }

    rippleOnSensor() {
        this.sensorIconNode = document.getElementById('lookup-sensor-icon');    
        animateSensorIcon(this.sensorIconNode);
    }

    goToSensorPage() {
        this.$state.go(States.SENSOR_DETAILS, {
            projectId: this.project.id,
            sensorId: this.device.id
        });
        this.DialogService.cancel()
    }

    saveProject({ project, patch }) {
        this.IAMService
            .updateProject(project.name, patch)
            .then(() => {
                const projectRef = this.projects.find(item => item.id === project.id);
                Object.assign(projectRef, project);

                if (this.ProjectManager.currentProjectId === project.id) {
                    this.ProjectManager.setCurrentProject(cloneProject(project));
                }

                this.toastService.showSimpleTranslated('project_was_updated');
            }, (serverResponse) => {
                this.toastService.showSimpleTranslated('project_wasnt_updated', {
                    serverResponse
                });
            });
    }

    static hasErrors(project) {
        return hasErrors(project);
    }

    static hasWarnings(project) {
        return hasWarnings(project) && !hasErrors(project);
    }

    closeModal() {
        this.DialogService.cancel()
    }

    $onDestroy() {
        if (this.eventSubscription) {
            this.eventSubscription.unsubscribe();
        }
    }

    // Map-related methods for project creation
    async initializeCreateProjectMap() {
        if (this.mapInitialized) return;
        
        try {
            await loadMapboxSDK();
            
            // Access token is limited to DT domains: *.disruptive-technologies.com
            mapboxgl.accessToken = Config.mapbox.accessToken; // eslint-disable-line
            this.createProjectMap = new mapboxgl.Map({ // eslint-disable-line
                container: 'create-project-map',
                style: 'mapbox://styles/mapbox/streets-v12',
                center: [0, 35],
                zoom: 0.1,
                projection: 'mercator' // Flat style
            });

            this.createProjectMarker = new mapboxgl.Marker({ // eslint-disable-line
                draggable: true
            });

            this.createProjectMarker.on('dragend', this.onCreateProjectDragEnd.bind(this));

            this.createProjectMap.on('click', (e) => {
                const lngLat = e.lngLat;
                this.createProjectGeocoder.clear();
                this.createProjectMarker.setLngLat(lngLat).addTo(this.createProjectMap);
                this.updateCreateProjectCoordinates(lngLat);
            });

            // Add Mapbox GL Geocoder for search functionality
            this.createProjectGeocoder = new MapboxGeocoder({ // eslint-disable-line
                accessToken: mapboxgl.accessToken, // eslint-disable-line
                mapboxgl: mapboxgl, // eslint-disable-line
                marker: false  // Disable the default marker
            });

            this.createProjectMap.addControl(this.createProjectGeocoder);

            this.createProjectGeocoder.on('result', (e) => {
                const lngLat = e.result.center;
                this.createProjectMap.flyTo({ center: lngLat, zoom: 8 });
                this.createProjectMarker.setLngLat(lngLat).addTo(this.createProjectMap);
                this.updateCreateProjectCoordinates({lng: lngLat[0], lat: lngLat[1]});
            });

            // Ensure the map is resized when the layout is populated
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 50);

            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 250);

            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 400);

            this.mapInitialized = true;
        } catch (error) {
            console.error('Error initializing map:', error); // eslint-disable-line no-console
        }
    }


    updateCreateProjectCoordinates(lngLat) {
        this.getCreateProjectTimeZone(lngLat);
    }

    onCreateProjectDragEnd() {
        const lngLat = this.createProjectMarker.getLngLat();
        this.updateCreateProjectCoordinates(lngLat);
    }

    getCreateProjectTimeZone(lngLat) {
        const lat = lngLat.lat;
        const lng = lngLat.lng;
        this.loadingCreateProjectTimeZone = true;

        // Use the Mapbox Tilequery API to get the time zone information
        fetch(`https://api.mapbox.com/v4/examples.4ze9z6tv/tilequery/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`) // eslint-disable-line
            .then(response => response.json())
            .then(data => {
                if (data && data.features && data.features.length > 0) {
                    const timeZoneFeature = data.features[0];
                    const timeZoneId = timeZoneFeature.properties.TZID;
                    // Immediately set the location
                    this.selectedLatitude = lat;
                    this.selectedLongitude = lng;
                    this.selectedTimeZone = timeZoneId;
                } else {
                    this.resetCreateProjectMarker();
                    this.toastService.showSimpleTranslated('project_no_valid_timezone_found');
                }
            })
            .catch(error => {
                console.error('Error fetching time zone:', error); // eslint-disable-line no-console
                this.resetCreateProjectMarker();
                this.toastService.showSimpleTranslated('project_timezone_wasnt_updated');
            })
            .finally(() => {
                this.loadingCreateProjectTimeZone = false;
                this.$rootScope.$applyAsync();
            });
    }

    resetCreateProjectMarker() {
        if (this.selectedLatitude && this.selectedLongitude) {
            this.createProjectMarker.setLngLat([this.selectedLongitude, this.selectedLatitude]).addTo(this.createProjectMap);
        } else {
            this.createProjectMarker.remove();
        }
    }

    clearCreateProjectLocation() {
        this.selectedTimeZone = null;
        this.selectedLatitude = null;
        this.selectedLongitude = null;
        this.createProjectMap.flyTo({ center: [0, 35], zoom: 0.1 });
        this.createProjectMarker.remove();
        this.createProjectGeocoder.clear();
        this.$rootScope.$applyAsync();
    }

    // Label management methods for project creation
    startAddLabel() {
        this.showAddLabelForm = true;
        this.newLabelKey = '';
        this.newLabelValue = '';
        
        // Focus the first input after the form is shown
        this.$timeout(() => {
            const keyInput = document.querySelector('.add-label-form input[ng-model="$ctrl.newLabelKey"]');
            if (keyInput) {
                keyInput.focus();
            }
        }, 0);
    }

    cancelAddLabel() {
        this.showAddLabelForm = false;
        this.newLabelKey = '';
        this.newLabelValue = '';
    }

    addNewProjectLabel() {
        if (this.newLabelKey && this.newLabelKey.trim()) {
            if (!this.projectLabels) {
                this.projectLabels = {};
            }
            const key = this.newLabelKey.trim();
            const value = this.newLabelValue ? this.newLabelValue.trim() : '';
            this.projectLabels[key] = value;
            this.cancelAddLabel();
        }
    }

    removeCreateProjectLabel(labelKey) {
        if (this.projectLabels && this.projectLabels[labelKey] !== undefined) {
            delete this.projectLabels[labelKey];
        }
    }

    // Autocomplete methods for label keys
    onKeyInputFocus() {
        if (this.availableProjectLabelKeys && this.availableProjectLabelKeys.length > 0) {
            this.filterLabelKeys();
            this.showKeyDropdown = true;
        }
    }

    onKeyInputBlur() {
        this.$timeout(() => {
            this.showKeyDropdown = false;
        }, 200);
    }

    onKeyInputChange() {
        if (this.availableProjectLabelKeys && this.availableProjectLabelKeys.length > 0) {
            this.filterLabelKeys();
            this.showKeyDropdown = true;
        }
    }

    filterLabelKeys() {
        if (!this.availableProjectLabelKeys) {
            this.filteredLabelKeys = [];
            return;
        }

        const query = (this.newLabelKey || '').toLowerCase();
        this.filteredLabelKeys = this.availableProjectLabelKeys.filter(item => 
            item.key.toLowerCase().includes(query)
        ).slice(0, 100);
        this.selectedKeyIndex = -1;
    }

    selectLabelKey(labelKey) {
        this.newLabelKey = labelKey;
        this.showKeyDropdown = false;
        // Focus on the value input after selecting a key
        this.$timeout(() => {
            const valueInput = document.querySelector('.add-label-form input[ng-model="$ctrl.newLabelValue"]');
            if (valueInput) {
                valueInput.focus();
            }
        }, 0);
    }

    // Autocomplete methods for label values
    onValueInputFocus() {
        if (this.newLabelKey && this.newLabelKey.trim()) {
            this.fetchLabelValues();
        }
    }

    onValueInputBlur() {
        this.$timeout(() => {
            this.showValueDropdown = false;
        }, 200);
    }

    onValueInputChange() {
        if (this.newLabelKey && this.newLabelKey.trim()) {
            this.filterLabelValues();
            this.showValueDropdown = this.filteredLabelValues.length > 0;
        }
    }

    fetchLabelValues() {
        if (!this.newLabelKey || !this.newLabelKey.trim()) {
            return;
        }

        this.SensorService.listLabelValues(this.newLabelKey.trim()).then(response => {
            this.availableLabelValues = [];
            if (response.projectLabelValues) {
                this.availableLabelValues.push(...response.projectLabelValues.map(item => ({
                    value: item.value,
                    deviceCount: item.deviceCount,
                    type: 'project'
                })));
            }
            this.availableLabelValues.sort((a, b) => b.deviceCount - a.deviceCount);
            
            this.filterLabelValues();
            this.showValueDropdown = this.filteredLabelValues.length > 0;
        }).catch(error => {
            console.error('Error fetching label values:', error); // eslint-disable-line no-console
            this.availableLabelValues = [];
            this.filteredLabelValues = [];
            this.showValueDropdown = false;
        });
    }

    filterLabelValues() {
        if (!this.availableLabelValues) {
            this.filteredLabelValues = [];
            return;
        }

        const query = (this.newLabelValue || '').toLowerCase();
        this.filteredLabelValues = this.availableLabelValues.filter(item => 
            item.value.toLowerCase().includes(query)
        ).slice(0, 100);
        this.selectedValueIndex = -1;
    }

    selectLabelValue(labelValue) {
        this.newLabelValue = labelValue;
        this.showValueDropdown = false;
    }
}
